import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Container, TextField, Button, InputAdornment,
    Alert, CircularProgress, Stack, Chip, IconButton, Tooltip,
    LinearProgress,
} from '@mui/material';
import {
    Email as EmailIcon,
    CheckCircle as CheckCircleIcon,
    ContentCopy as CopyIcon,
    Twitter as TwitterIcon,
    WhatsApp as WhatsAppIcon,
    ArrowForward as ArrowIcon,
    ArrowBack as BackIcon,
    AutoAwesome as SparkleIcon,
    Group as GroupIcon,
    TrendingUp as TrendingIcon,
    Lightbulb as IdeaIcon,
    Schedule as ScheduleIcon,
    Analytics as AnalyticsIcon,
    Forum as EngageIcon,
    Instagram as InstagramIcon,
    YouTube as YouTubeIcon,
    Facebook as FacebookIcon,
} from '@mui/icons-material';
import { newsletterApi } from '../../services/api';

// ── Constants ──────────────────────────────────────────────────────────────────

const ROLES = [
    { id: 'agency',          label: 'Agency Owner',         icon: '🏢', desc: 'Managing multiple client accounts' },
    { id: 'social_manager',  label: 'Social Media Manager', icon: '📱', desc: 'Running brand social channels' },
    { id: 'business_owner',  label: 'Business Owner',       icon: '💼', desc: 'Growing my own brand' },
    { id: 'content_creator', label: 'Content Creator',      icon: '🎨', desc: 'Building my personal audience' },
];

const PLATFORMS = [
    { id: 'instagram', label: 'Instagram', icon: <InstagramIcon sx={{ fontSize: 18 }} /> },
    { id: 'youtube',   label: 'YouTube',   icon: <YouTubeIcon   sx={{ fontSize: 18 }} /> },
    { id: 'facebook',  label: 'Facebook',  icon: <FacebookIcon  sx={{ fontSize: 18 }} /> },
    { id: 'tiktok',    label: 'TikTok',    icon: <SparkleIcon   sx={{ fontSize: 18 }} /> },
    { id: 'linkedin',  label: 'LinkedIn',  icon: <GroupIcon     sx={{ fontSize: 18 }} /> },
    { id: 'twitter',   label: 'Twitter/X', icon: <TwitterIcon   sx={{ fontSize: 18 }} /> },
];

const PAIN_POINTS = [
    { id: 'ideas',      label: 'Coming up with ideas',    icon: <IdeaIcon      sx={{ fontSize: 20 }} /> },
    { id: 'scheduling', label: 'Consistent scheduling',   icon: <ScheduleIcon  sx={{ fontSize: 20 }} /> },
    { id: 'analytics',  label: 'Understanding analytics', icon: <AnalyticsIcon sx={{ fontSize: 20 }} /> },
    { id: 'engagement', label: 'Growing engagement',      icon: <EngageIcon    sx={{ fontSize: 20 }} /> },
    { id: 'all',        label: 'All of the above 😅',     icon: <TrendingIcon  sx={{ fontSize: 20 }} /> },
];

// ── Animated count-up hook ─────────────────────────────────────────────────────

function useCountUp(target, duration = 1200) {
    const [count, setCount] = useState(0);
    useEffect(() => {
        if (!target) return;
        let current = 0;
        const step = target / (duration / 16);
        const timer = setInterval(() => {
            current = Math.min(current + step, target);
            setCount(Math.floor(current));
            if (current >= target) clearInterval(timer);
        }, 16);
        return () => clearInterval(timer);
    }, [target, duration]);
    return count;
}

// ── Shared sub-components ──────────────────────────────────────────────────────

function ProgressDots({ step }) {
    return (
        <Stack direction="row" spacing={1} justifyContent="center" sx={{ mb: 4 }}>
            {[1, 2, 3].map(s => (
                <Box key={s} sx={{
                    width: s === step ? 24 : 8, height: 8,
                    borderRadius: '100px',
                    bgcolor: s === step ? '#6366f1' : s < step ? 'rgba(99,102,241,0.4)' : 'rgba(255,255,255,0.1)',
                    transition: 'all 0.3s ease',
                }} />
            ))}
        </Stack>
    );
}

function SelectCard({ selected, onClick, children }) {
    return (
        <Box onClick={onClick} sx={{
            p: 2, borderRadius: '12px', cursor: 'pointer',
            border: `1.5px solid ${selected ? '#6366f1' : 'rgba(255,255,255,0.08)'}`,
            bgcolor: selected ? 'rgba(99,102,241,0.12)' : 'rgba(255,255,255,0.02)',
            boxShadow: selected ? '0 0 0 1px #6366f1' : 'none',
            transition: 'all 0.2s',
            '&:hover': { bgcolor: 'rgba(99,102,241,0.07)', borderColor: 'rgba(99,102,241,0.4)' },
        }}>
            {children}
        </Box>
    );
}

// ── Step 1: Email capture ──────────────────────────────────────────────────────

function StepEmail({ onNext, subscriberCount }) {
    const [email,   setEmail]   = useState('');
    const [loading, setLoading] = useState(false);
    const [error,   setError]   = useState(null);
    const animatedCount = useCountUp(subscriberCount);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            setError('Please enter a valid email address.');
            return;
        }
        setLoading(true); setError(null);
        try {
            const ref = new URLSearchParams(window.location.search).get('ref') || null;
            const res = await newsletterApi.subscribe(
                email, { source: 'landing_page_waitlist', signup_type: 'waitlist' }, ref
            );
            onNext({ email, ...res });
        } catch (err) {
            const is429 = err.response?.status === 429;
            setError(is429
                ? 'Too many attempts. Please wait and try again.'
                : (err.message || 'Something went wrong.'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box>
            <ProgressDots step={1} />

            <Typography variant="overline" sx={{ fontWeight: 800, color: '#6366f1', letterSpacing: '0.2em', fontSize: '0.75rem' }}>
                MARKET VERIFICATION &amp; WAITLIST
            </Typography>
            <Typography variant="h2" sx={{ fontWeight: 900, fontSize: { xs: '2.2rem', md: '3.5rem' }, letterSpacing: '-0.04em', mt: 2, mb: 2, lineHeight: 1.1 }}>
                Ready to automate your growth?
            </Typography>

            {subscriberCount > 5 && (
                <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="center" sx={{ mb: 3 }}>
                    {/* Stacked avatars */}
                    <Stack direction="row">
                        {['#6366f1','#a855f7','#10d98a','#f59e0b'].map((c, i) => (
                            <Box key={i} sx={{
                                width: 28, height: 28, borderRadius: '50%',
                                bgcolor: c, border: '2px solid #0a0a14',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: '0.6rem', fontWeight: 900, color: 'white',
                                ml: i === 0 ? 0 : -1, zIndex: 4 - i,
                            }}>
                                {['A','J','K','M'][i]}
                            </Box>
                        ))}
                    </Stack>
                    <Typography variant="body2" sx={{ color: '#10d98a', fontWeight: 800, letterSpacing: '0.04em' }}>
                        {animatedCount.toLocaleString()}+ ALREADY ON THE LIST
                    </Typography>
                </Stack>
            )}

            <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '1.1rem', mb: 5, maxWidth: 520, mx: 'auto', lineHeight: 1.6 }}>
                Join the restricted waitlist. Launching in batches to ensure every user gets high-performance AI attention.
            </Typography>

            <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: 500, mx: 'auto' }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField
                        fullWidth
                        placeholder="Enter your email address"
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        disabled={loading}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                bgcolor: 'rgba(255,255,255,0.03)', borderRadius: '100px',
                                height: 58, px: 1,
                                border: '1px solid rgba(255,255,255,0.1)',
                                '&:hover':       { border: '1px solid rgba(255,255,255,0.2)' },
                                '&.Mui-focused': { border: '1px solid #6366f1', boxShadow: '0 0 20px rgba(99,102,241,0.2)' },
                            },
                            '& input':    { color: 'white', fontWeight: 600, px: 2 },
                            '& fieldset': { border: 'none' },
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <EmailIcon sx={{ color: 'rgba(255,255,255,0.3)', ml: 1 }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={loading}
                        endIcon={!loading && <ArrowIcon />}
                        sx={{
                            minWidth: { xs: '100%', sm: 180 }, height: 58,
                            borderRadius: '100px', bgcolor: '#6366f1', color: 'white',
                            fontWeight: 800, fontSize: '1rem',
                            boxShadow: '0 10px 20px -5px rgba(99,102,241,0.4)',
                            '&:hover': { bgcolor: '#818cf8', transform: 'translateY(-2px)' },
                            transition: 'all 0.3s',
                        }}
                    >
                        {loading ? <CircularProgress size={22} color="inherit" /> : 'Get Early Access'}
                    </Button>
                </Stack>

                {error && (
                    <Alert severity="error" onClose={() => setError(null)} sx={{ mt: 3, borderRadius: '12px', bgcolor: 'rgba(244,67,54,0.08)', color: '#ff8a80', border: '1px solid rgba(244,67,54,0.2)', '& .MuiAlert-icon': { color: '#ff8a80' } }}>
                        {error}
                    </Alert>
                )}
                <Typography variant="caption" sx={{ display: 'block', mt: 3, color: 'rgba(255,255,255,0.25)', lineHeight: 1.5 }}>
                    By joining, you agree to receive product updates. Unsubscribe any time.
                </Typography>
            </Box>
        </Box>
    );
}

// ── Step 1.5: Check your email (double opt-in) ────────────────────────────────

function StepCheckEmail({ email, refLink, onReset }) {
    const shareUrl  = refLink || window.location.origin;
    const shareText = encodeURIComponent(
        `I just joined the Socialixer waitlist — AI-powered social media automation. Join me: ${shareUrl}&utm_source=twitter&utm_medium=social&utm_campaign=waitlist_referral`
    );

    return (
        <Box>
            <ProgressDots step={2} />

            <Box sx={{ mb: 4 }}>
                <Typography sx={{ fontSize: '3rem', mb: 2 }}>📬</Typography>
                <Typography variant="h3" sx={{ fontWeight: 900, letterSpacing: '-0.03em', mb: 2 }}>
                    Check your inbox!
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '1.05rem', lineHeight: 1.7, maxWidth: 420, mx: 'auto' }}>
                    We sent a verification link to{' '}
                    <strong style={{ color: 'white' }}>{email}</strong>.
                    <br />
                    Click it to confirm your spot on the waitlist.
                </Typography>
            </Box>

            <Box sx={{
                p: 3, mb: 4, borderRadius: '16px',
                background: 'linear-gradient(135deg, rgba(16,217,138,0.08), rgba(99,102,241,0.08))',
                border: '1px solid rgba(16,217,138,0.2)',
            }}>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', mb: 0.5, fontWeight: 700, fontSize: '0.75rem', letterSpacing: '0.1em' }}>
                    WHILE YOU WAIT
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.65)', lineHeight: 1.6 }}>
                    Share your referral link to move up the queue — every friend who signs up bumps you higher.
                </Typography>
            </Box>

            <Stack direction="row" spacing={1.5} justifyContent="center" sx={{ mb: 4 }}>
                <Button
                    href={`https://twitter.com/intent/tweet?text=${shareText}`}
                    target="_blank" rel="noopener noreferrer"
                    startIcon={<TwitterIcon />} size="small"
                    sx={{ borderRadius: '100px', color: 'white', fontWeight: 700, textTransform: 'none', bgcolor: 'rgba(29,161,242,0.15)', border: '1px solid rgba(29,161,242,0.25)', '&:hover': { bgcolor: 'rgba(29,161,242,0.25)' } }}
                >
                    Share on X
                </Button>
                <Button
                    href={`https://wa.me/?text=${encodeURIComponent(`I just joined the Socialixer waitlist — join me: ${shareUrl}&utm_source=whatsapp&utm_medium=social&utm_campaign=waitlist_referral`)}`}
                    target="_blank" rel="noopener noreferrer"
                    startIcon={<WhatsAppIcon />} size="small"
                    sx={{ borderRadius: '100px', color: 'white', fontWeight: 700, textTransform: 'none', bgcolor: 'rgba(37,211,102,0.12)', border: '1px solid rgba(37,211,102,0.2)', '&:hover': { bgcolor: 'rgba(37,211,102,0.22)' } }}
                >
                    Share on WhatsApp
                </Button>
            </Stack>

            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.2)', display: 'block', mb: 2 }}>
                Didn't receive the email? Check your spam folder or{' '}
                <Box component="span" onClick={onReset} sx={{ color: '#6366f1', cursor: 'pointer', textDecoration: 'underline' }}>
                    try a different address
                </Box>.
            </Typography>
        </Box>
    );
}

// ── Step 2: Onboarding questions ───────────────────────────────────────────────

function StepOnboarding({ email, onNext, onBack }) {
    const [role,      setRole]      = useState('');
    const [platforms, setPlatforms] = useState([]);
    const [painPoint, setPainPoint] = useState('');
    const [loading,   setLoading]   = useState(false);
    const [error,     setError]     = useState(null);
    const [subStep,   setSubStep]   = useState(0); // 0=role, 1=platforms, 2=pain
    const [saveError, setSaveError] = useState(false);

    const togglePlatform = id =>
        setPlatforms(prev => prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]);

    const handleNext = async () => {
        if (subStep === 0 && !role)           { setError('Please select your role.'); return; }
        if (subStep === 1 && !platforms.length) { setError('Select at least one platform.'); return; }
        if (subStep < 2) { setError(null); setSubStep(s => s + 1); return; }
        if (!painPoint)  { setError('Please pick your biggest challenge.'); return; }

        setLoading(true); setError(null); setSaveError(false);
        try {
            const res = await newsletterApi.saveOnboarding(email, { role, platforms, painPoint });
            onNext(res);
        } catch {
            // Onboarding save failed — show error instead of silently succeeding
            setSaveError(true);
            setLoading(false);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box>
            <ProgressDots step={2} />
            <LinearProgress
                variant="determinate"
                value={((subStep + 1) / 3) * 100}
                sx={{ mb: 4, height: 3, borderRadius: '100px', bgcolor: 'rgba(255,255,255,0.06)', '& .MuiLinearProgress-bar': { bgcolor: '#6366f1', borderRadius: '100px' } }}
            />

            {subStep === 0 && (
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 900, mb: 1, letterSpacing: '-0.02em' }}>What best describes you?</Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.4)', mb: 4 }}>This helps us personalise your experience.</Typography>
                    <Stack spacing={2}>
                        {ROLES.map(r => (
                            <SelectCard key={r.id} selected={role === r.id} onClick={() => setRole(r.id)}>
                                <Stack direction="row" alignItems="center" spacing={2}>
                                    <Box sx={{ fontSize: '1.5rem' }}>{r.icon}</Box>
                                    <Box sx={{ textAlign: 'left' }}>
                                        <Typography sx={{ fontWeight: 700, color: 'white' }}>{r.label}</Typography>
                                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>{r.desc}</Typography>
                                    </Box>
                                </Stack>
                            </SelectCard>
                        ))}
                    </Stack>
                </Box>
            )}

            {subStep === 1 && (
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 900, mb: 1, letterSpacing: '-0.02em' }}>Which platforms do you manage?</Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.4)', mb: 4 }}>Select all that apply.</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, justifyContent: 'center' }}>
                        {PLATFORMS.map(p => (
                            <SelectCard key={p.id} selected={platforms.includes(p.id)} onClick={() => togglePlatform(p.id)}>
                                <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 0.5 }}>
                                    <Box sx={{ color: platforms.includes(p.id) ? '#6366f1' : 'rgba(255,255,255,0.5)' }}>{p.icon}</Box>
                                    <Typography sx={{ fontWeight: 700, color: 'white', fontSize: '0.9rem' }}>{p.label}</Typography>
                                </Stack>
                            </SelectCard>
                        ))}
                    </Box>
                </Box>
            )}

            {subStep === 2 && (
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 900, mb: 1, letterSpacing: '-0.02em' }}>What's your biggest challenge?</Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.4)', mb: 4 }}>We'll build features around what matters most to you.</Typography>
                    <Stack spacing={2}>
                        {PAIN_POINTS.map(p => (
                            <SelectCard key={p.id} selected={painPoint === p.id} onClick={() => setPainPoint(p.id)}>
                                <Stack direction="row" alignItems="center" spacing={2}>
                                    <Box sx={{ color: painPoint === p.id ? '#6366f1' : 'rgba(255,255,255,0.4)' }}>{p.icon}</Box>
                                    <Typography sx={{ fontWeight: 700, color: 'white', textAlign: 'left' }}>{p.label}</Typography>
                                </Stack>
                            </SelectCard>
                        ))}
                    </Stack>
                </Box>
            )}

            {saveError && (
                <Alert severity="error" sx={{ mt: 3, borderRadius: '12px', bgcolor: 'rgba(244,67,54,0.08)', color: '#ff8a80', border: '1px solid rgba(244,67,54,0.2)', '& .MuiAlert-icon': { color: '#ff8a80' } }}>
                    Something went wrong saving your answers. Please check your connection and try again.
                </Alert>
            )}

            {error && !saveError && (
                <Alert severity="error" sx={{ mt: 3, borderRadius: '12px', bgcolor: 'rgba(244,67,54,0.08)', color: '#ff8a80', border: '1px solid rgba(244,67,54,0.15)', '& .MuiAlert-icon': { color: '#ff8a80' } }}>
                    {error}
                </Alert>
            )}

            <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
                <Button
                    onClick={subStep > 0 ? () => { setSubStep(s => s - 1); setError(null); } : onBack}
                    startIcon={<BackIcon />}
                    sx={{ color: 'rgba(255,255,255,0.4)', textTransform: 'none', fontWeight: 700, '&:hover': { color: 'white' } }}
                >
                    Back
                </Button>
                <Button
                    onClick={handleNext}
                    disabled={loading}
                    variant="contained"
                    endIcon={!loading && (subStep < 2 ? <ArrowIcon /> : <CheckCircleIcon />)}
                    sx={{
                        flex: 1, height: 52, borderRadius: '100px',
                        bgcolor: '#6366f1', fontWeight: 800, fontSize: '1rem',
                        boxShadow: '0 8px 20px -5px rgba(99,102,241,0.4)',
                        '&:hover': { bgcolor: '#818cf8' },
                    }}
                >
                    {loading ? <CircularProgress size={20} color="inherit" /> : subStep < 2 ? 'Next' : 'Finish — Get My Spot'}
                </Button>
            </Stack>
        </Box>
    );
}

// ── Step 3: Success + Referral ─────────────────────────────────────────────────

function StepSuccess({ email, queuePosition, refLink, segment, onReset }) {
    const [copied, setCopied] = useState(false);
    const animatedPos = useCountUp(queuePosition, 800);
    const shareUrl  = refLink || window.location.origin;
    const utmRef    = `${shareUrl}${shareUrl.includes('?') ? '&' : '?'}utm_source=twitter&utm_medium=social&utm_campaign=waitlist_referral`;
    const shareText = encodeURIComponent(`I just joined the Socialixer waitlist — AI-powered social media automation. Join me: ${utmRef}`);

    const handleCopy = useCallback(() => {
        navigator.clipboard.writeText(shareUrl)
            .then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 2500);
            })
            .catch(() => {
                // Clipboard write not available (e.g. non-HTTPS) — fail silently
            });
    }, [shareUrl]);

    return (
        <Box>
            <ProgressDots step={3} />

            <Box sx={{ mb: 4 }}>
                <Typography sx={{ fontSize: '3.5rem', mb: 1 }}>🎉</Typography>
                <Typography variant="h3" sx={{ fontWeight: 900, letterSpacing: '-0.03em', mb: 1 }}>You're in!</Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '1.05rem' }}>
                    Check your inbox — we've sent a confirmation to{' '}
                    <strong style={{ color: 'white' }}>{email}</strong>
                </Typography>
            </Box>

            {/* Queue position card */}
            {queuePosition && (
                <Box sx={{
                    p: 3, mb: 4, borderRadius: '16px',
                    background: 'linear-gradient(135deg, rgba(99,102,241,0.15) 0%, rgba(168,85,247,0.1) 100%)',
                    border: '1px solid rgba(99,102,241,0.25)',
                }}>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase' }}>
                        Your Queue Position
                    </Typography>
                    <Typography variant="h2" sx={{ fontWeight: 900, color: '#6366f1', fontSize: '3.5rem', lineHeight: 1 }}>
                        #{animatedPos.toLocaleString()}
                    </Typography>
                    {segment && segment !== 'General' && (
                        <Chip label={segment} size="small" sx={{ mt: 1, bgcolor: 'rgba(99,102,241,0.2)', color: '#a5b4fc', fontWeight: 700, borderRadius: '6px' }} />
                    )}
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.4)', mt: 1.5, fontSize: '0.85rem' }}>
                        Share your link below to move up the queue ↓
                    </Typography>
                </Box>
            )}

            {/* Referral link */}
            {refLink && (
                <Box sx={{ mb: 4 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: 'rgba(255,255,255,0.7)' }}>
                        🔗 Your referral link — each friend you invite moves you up
                    </Typography>
                    <Stack direction="row" spacing={1}>
                        <Box sx={{
                            flex: 1, p: 1.5, px: 2, borderRadius: '12px',
                            bgcolor: 'rgba(255,255,255,0.04)',
                            border: '1px solid rgba(255,255,255,0.1)',
                            overflow: 'hidden',
                        }}>
                            <Typography noWrap sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem', fontFamily: 'monospace' }}>
                                {shareUrl}
                            </Typography>
                        </Box>
                        <Tooltip title={copied ? 'Copied!' : 'Copy link'}>
                            <IconButton
                                onClick={handleCopy}
                                aria-label="Copy referral link"
                                sx={{
                                    bgcolor: copied ? 'rgba(16,217,138,0.15)' : 'rgba(99,102,241,0.15)',
                                    color: copied ? '#10d98a' : '#a5b4fc',
                                    borderRadius: '12px',
                                    '&:hover': { bgcolor: 'rgba(99,102,241,0.25)' },
                                }}
                            >
                                {copied ? <CheckCircleIcon /> : <CopyIcon />}
                            </IconButton>
                        </Tooltip>
                    </Stack>

                    <Stack direction="row" spacing={1.5} sx={{ mt: 2, justifyContent: 'center' }}>
                        <Button
                            href={`https://twitter.com/intent/tweet?text=${shareText}`}
                            target="_blank" rel="noopener noreferrer"
                            component="a"
                            startIcon={<TwitterIcon />} size="small"
                            sx={{ borderRadius: '100px', color: 'white', fontWeight: 700, textTransform: 'none', bgcolor: 'rgba(29,161,242,0.15)', border: '1px solid rgba(29,161,242,0.25)', '&:hover': { bgcolor: 'rgba(29,161,242,0.25)' } }}
                        >
                            Share on X
                        </Button>
                        <Button
                            href={`https://wa.me/?text=${encodeURIComponent(`I just joined the Socialixer waitlist — join me: ${shareUrl}${shareUrl.includes('?') ? '&' : '?'}utm_source=whatsapp&utm_medium=social&utm_campaign=waitlist_referral`)}`}
                            target="_blank" rel="noopener noreferrer"
                            component="a"
                            startIcon={<WhatsAppIcon />} size="small"
                            sx={{ borderRadius: '100px', color: 'white', fontWeight: 700, textTransform: 'none', bgcolor: 'rgba(37,211,102,0.12)', border: '1px solid rgba(37,211,102,0.2)', '&:hover': { bgcolor: 'rgba(37,211,102,0.22)' } }}
                        >
                            Share on WhatsApp
                        </Button>
                    </Stack>
                </Box>
            )}

            <Button onClick={onReset} sx={{ color: 'rgba(255,255,255,0.3)', textTransform: 'none', '&:hover': { color: 'rgba(255,255,255,0.6)' } }}>
                Sign up another email
            </Button>
        </Box>
    );
}

// ── Main NewsletterSection ─────────────────────────────────────────────────────

const NewsletterSection = () => {
    // step: 1=email form, 1.5='check_email', 2=onboarding, 3=success
    const [step,            setStep]            = useState(1);
    const [subscriberCount, setSubscriberCount] = useState(null);
    const [signupData,      setSignupData]      = useState(null);
    const [onboardingData,  setOnboardingData]  = useState(null);

    useEffect(() => {
        newsletterApi.getCount()
            .then(d => setSubscriberCount(d.count))
            .catch(() => {});
    }, []);

    const handleStep1Complete = (data) => {
        setSignupData(data);
        if (data.alreadySubscribed) {
            // Already active — jump to success
            setOnboardingData({ queuePosition: null, refLink: data.refLink, segment: '' });
            setStep(3);
        } else {
            // New subscriber (with or without verification) — show onboarding first
            setStep(2);
        }
    };

    const handleStep2Complete = (data) => {
        setOnboardingData(data);
        if (signupData?.requiresVerification) {
            // Double opt-in flow — send to "check inbox" after onboarding
            setStep(1.5);
        } else {
            setStep(3);
        }
    };

    const handleReset = () => {
        setStep(1);
        setSignupData(null);
        setOnboardingData(null);
    };

    return (
        <Box id="waitlist" sx={{ py: { xs: 10, md: 16 }, position: 'relative', overflow: 'hidden' }}>
            <Box sx={{
                position: 'absolute', top: '50%', left: '50%',
                transform: 'translate(-50%,-50%)',
                width: '80vw', height: '40vh',
                background: 'radial-gradient(circle, rgba(99,102,241,0.08) 0%, transparent 70%)',
                filter: 'blur(80px)', pointerEvents: 'none',
            }} />

            <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
                <Box sx={{
                    p: { xs: 4, md: 7 }, borderRadius: '24px',
                    bgcolor: 'rgba(255,255,255,0.02)',
                    border: '1px solid rgba(255,255,255,0.07)',
                    backdropFilter: 'blur(16px)', textAlign: 'center',
                    boxShadow: '0 40px 100px rgba(0,0,0,0.4)',
                    animation: 'fadeSlideIn 0.35s ease-out',
                }}>
                    {step === 1    && <StepEmail       onNext={handleStep1Complete} subscriberCount={subscriberCount} />}
                    {step === 1.5  && <StepCheckEmail  email={signupData?.email}    refLink={signupData?.refLink} onReset={handleReset} />}
                    {step === 2    && <StepOnboarding  email={signupData?.email}    onNext={handleStep2Complete} onBack={() => setStep(1)} />}
                    {step === 3    && (
                        <StepSuccess
                            email={signupData?.email}
                            queuePosition={onboardingData?.queuePosition ?? signupData?.queuePosition}
                            refLink={onboardingData?.refLink ?? signupData?.refLink}
                            segment={onboardingData?.segment}
                            onReset={handleReset}
                        />
                    )}
                </Box>
            </Container>

            <style>{`
                @keyframes fadeSlideIn {
                    from { opacity: 0; transform: translateY(12px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
            `}</style>
        </Box>
    );
};

export default NewsletterSection;
